import { useState, useEffect } from "react";
import { Timestamp } from "firebase/firestore";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Chip } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { keyframes } from "@emotion/react";
import { Box } from "@mui/system";

dayjs.extend(relativeTime);

// Animation for color and scale
const scaleAndColorAnimation = keyframes`
  0% { transform: scale(1); color: black; border-color: black; }
  50% { transform: scale(1.1); color: #E35D5D; border-color: #E35D5D; } /* Dark Coral */
  100% { transform: scale(1); color: black; border-color: black; }
`;

const CountdownTimer = ({ endDate, isClosed }: { endDate: Date, isClosed: boolean }) => {
    const [timeLeft, setTimeLeft] = useState<string | null>(null);
    const [isCritical, setIsCritical] = useState(false);

    useEffect(() => {
        const updateTimer = () => {
            if (!endDate) {
                setTimeLeft(null);
                return;
            }
            const now = dayjs();
            const endDateJS = dayjs(endDate);
            const diffInSeconds = endDateJS.diff(now, "second");

              if (diffInSeconds <= 0) {
                setTimeLeft("Expired");
                return;
            }
            if (diffInSeconds <= 7200) { // Less than 2 hours
                const hours = Math.floor(diffInSeconds / 3600);
                const minutes = Math.floor((diffInSeconds % 3600) / 60);
                const seconds = diffInSeconds % 60;

                setTimeLeft(`${hours}h ${minutes}m ${seconds}s left`);
                setIsCritical(diffInSeconds <= 1800); // If less than 30 min, enable animation
            } else {
                setTimeLeft(null);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000); // Update every second

        return () => clearInterval(interval);
    }, [endDate]);

    const endDateJS = endDate;
    const today = dayjs().startOf("day");

    const now = dayjs();
    const timeUntilEnd = dayjs(endDateJS).diff(now, 'minute');
    const showComponent = timeUntilEnd <= 120 && timeUntilEnd > 0;

    const formatDate = (date: dayjs.Dayjs) => {
        if (date.isSame(today, "day")) return `Today ${date.format("h:mm A")}`;
        if (date.isSame(today.add(1, "day"), "day")) return `Tomorrow ${date.format("h:mm A")}`;
        return date.format("YYYY-MM-DD : h:mm A");
    };

    return (
        <>
            {isClosed === true || !endDate ? (
                <div />
            ) :
                timeLeft === "Expired" ? (
                    <Chip
                        label="Expired"
                        sx={{
                            fontWeight: "bold",
                            borderRadius: "16px",
                            fontSize: "0.9rem",
                            padding: "6px 12px",
                            borderWidth: 2,
                            backgroundColor: "#E35D5D", // Dark Coral
                            color: "#fff",
                            borderColor: "#E35D5D",
                            '& .MuiChip-label': {
                                display: 'block',
                                whiteSpace: 'normal',
                                textAlign: 'left'
                            },
                        }}
                        variant="outlined"
                    />
                ) : timeLeft ? (
                    <Chip
                        icon={
                            <AccessTimeIcon
                                sx={{
                                    color: "inherit", // Inherit animated color
                                    transition: "color 0.5s ease-in-out",
                                }}
                            />
                        }
                        label={
                            <Box sx={{ whiteSpace: 'normal', lineHeight: 1.2 }}>
                                <div><strong>Ending soon:</strong> {timeLeft}</div>
                            </Box>
                        }
                        sx={{
                            fontWeight: "bold",
                            borderRadius: "16px",
                            fontSize: "0.9rem",
                            padding: "6px 12px",
                            borderWidth: 2,
                            backgroundColor: "transparent",
                            marginBottom: "8px",
                            animation: isCritical ? `${scaleAndColorAnimation} 1s infinite alternate` : "none",
                            transition: "color 0.5s ease-in-out, border-color 0.5s ease-in-out",
                            color: isCritical ? "black" : "#E35D5D",
                            borderColor: isCritical ? "black" : "#E35D5D",
                            '& .MuiChip-label': {
                                display: 'block',
                                whiteSpace: 'normal',
                                textAlign: 'left'
                            },
                        }}
                        variant="outlined"
                    />
                ) : (
                    <></>
                )}
        </>
    );
};

export default CountdownTimer;
